import { createAction } from '@reduxjs/toolkit';
import { ref, remove, update } from 'firebase/database';

import { database } from '../../firebase.utils';
import {
  createAddStudentHandler,
  createRenewTestIDStudentHandler,
} from '../common/student';
import { createNotification } from '../notifications/notifications.actions';

export const [
  getCoordinatorsStarted,
  getCoordinatorsReceived,
  getCoordinatorsFailed,
] = [
  createAction('GET_COORDINATORS_STARTED'),
  createAction('GET_COORDINATORS_RECEIVED'),
  createAction('GET_COORDINATORS_FAILED'),
];

export const [getTestsStarted, getTestsReceived, getTestsFailed] = [
  createAction('GET_TESTS_STARTED'),
  createAction('GET_TESTS_RECEIVED'),
  createAction('GET_TESTS_FAILED'),
];

export const [
  reopenRegistrationStarted,
  reopenRegistrationReceived,
  reopenRegistrationFailed,
] = [
  createAction('REOPEN_REGISTRATION_STARTED'),
  createAction('REOPEN_REGISTRATION_RECEIVED'),
  createAction('REOPEN_REGISTRATION_FAILED'),
];

export const reopenRegistration =
  ({ uid }) =>
  (dispatch) => {
    dispatch(reopenRegistrationStarted());
    const updates = {};
    updates['/finished'] = null;
    updates['/billing'] = null;
    update(ref(database, '/koordinator/' + uid), updates)
      .then(() => {
        dispatch(reopenRegistrationReceived());
        dispatch(
          createNotification({
            message: `Registrácia bola otvorená`,
            severity: 'success',
          })
        );
      })
      .catch((error) => {
        dispatch(reopenRegistrationFailed());
        dispatch(
          createNotification({
            message: `${error.code}: ${error.message}`,
            severity: 'error',
          })
        );
      });
  };

export const [addStudentStarted, addStudentReceived, addStudentFailed] = [
  createAction('ADMIN_ADD_STUDENT_STARTED'),
  createAction('ADMIN_ADD_STUDENT_RECEIVED'),
  createAction('ADMIN_ADD_STUDENT_FAILED'),
];

export const addStudent = (cid, student_data) =>
  createAddStudentHandler(student_data, cid);

export const [
  renewTestIDStudentStarted,
  renewTestIDStudentReceived,
  renewTestIDStudentFailed,
] = [
  createAction('ADMIN_RENEW_TEST_ID_STUDENT_STARTED'),
  createAction('ADMIN_RENEW_TEST_ID_STUDENT_RECEIVED'),
  createAction('ADMIN_RENEW_TEST_ID_STUDENT_FAILED'),
];

export const reniewTestIDStudent = (cid, sid) =>
  createRenewTestIDStudentHandler(cid, sid);

export const [
  removeStudentStarted,
  removeStudentReceived,
  removeStudentFailed,
] = [
  createAction('ADMIN_REMOVE_STUDENT_STARTED'),
  createAction('ADMIN_REMOVE_STUDENT_RECEIVED'),
  createAction('ADMIN_REMOVE_STUDENT_FAILED'),
];

export const removeStudent = (cid, sid) => (dispatch) => {
  dispatch(removeStudentStarted());
  remove(ref(database, '/koordinator/' + cid + '/students/' + sid))
    .then(() => {
      dispatch(removeStudentReceived());
      dispatch(
        createNotification({
          message: `Žiak bol odstránený`,
          severity: 'warning',
          timeout: 5000,
        })
      );
    })
    .catch((error) => {
      dispatch(removeStudentFailed());
      dispatch(
        createNotification({
          message: `${error.code}: ${error.message}`,
          severity: 'error',
        })
      );
    });
};

export const [
  updateStudentScoreStarted,
  updateStudentScoreReceived,
  updateStudentScoreFailed,
] = [
  createAction('UPDATE_STUDENT_SCORE_STARTED'),
  createAction('UPDATE_STUDENT_SCORE_RECEIVED'),
  createAction('UPDATE_STUDENT_SCORE_FAILED'),
];

export const updateStudentScore =
  (cid, { points, sid }) =>
  (dispatch) => {
    dispatch(updateStudentScoreStarted());
    const updates = {};
    updates['/percentage'] = points;
    updates['/points'] = points;
    update(ref(database, '/koordinator/' + cid + '/students/' + sid), updates)
      .then(() => {
        dispatch(updateStudentScoreReceived());
      })
      .catch((error) => {
        dispatch(updateStudentScoreFailed());
        dispatch(
          createNotification({
            message: `${error.code}: ${error.message}`,
            severity: 'error',
          })
        );
      });
  };

export const [
  updateStudentStarted,
  updateStudentReceived,
  updateStudentFailed,
] = [
  createAction('ADMIN_UPDATE_STUDENT_STARTED'),
  createAction('ADMIN_UPDATE_STUDENT_RECEIVED'),
  createAction('ADMIN_UPDATE_STUDENT_FAILED'),
];

export const updateStudent =
  (cid, { classroom, firstname, lastname, percentage, points, sid }) =>
  (dispatch) => {
    dispatch(updateStudentStarted());
    const updates = {};
    updates['/firstname'] = firstname;
    updates['/lastname'] = lastname;
    updates['/classroom'] = classroom;
    updates['/percentage'] = points;
    updates['/points'] = points;
    update(ref(database, '/koordinator/' + cid + '/students/' + sid), updates)
      .then(() => {
        dispatch(updateStudentReceived());
        dispatch(
          createNotification({
            message: `Žiak bol zmenený`,
            severity: 'success',
          })
        );
      })
      .catch((error) => {
        dispatch(updateStudentFailed());
        dispatch(
          createNotification({
            message: `${error.code}: ${error.message}`,
            severity: 'error',
          })
        );
      });
  };

export const [
  finishRegistrationStarted,
  finishRegistrationReceived,
  finishRegistrationFailed,
] = [
  createAction('ADMIN_FINISH_REGISTRATION_STARTED'),
  createAction('ADMIN_FINISH_REGISTRATION_RECEIVED'),
  createAction('ADMIN_FINISH_REGISTRATION_FAILED'),
];

export const finishRegistration =
  ({ billingAddr, email, payment, uid }) =>
  (dispatch) => {
    dispatch(finishRegistrationStarted());
    const updates = {};
    updates['/finished'] = true;
    updates['/billing/billingAddr'] = billingAddr;
    updates['/billing/finished'] = true;
    updates['/billing/email'] = email;
    updates['/billing/payment'] = payment;
    update(ref(database, '/koordinator/' + uid), updates)
      .then(() => {
        dispatch(finishRegistrationReceived());
        dispatch(
          createNotification({
            message: `Registrácia bola uzavretá`,
            severity: 'success',
          })
        );
      })
      .catch((error) => {
        dispatch(finishRegistrationFailed());
        dispatch(
          createNotification({
            message: `${error.code}: ${error.message}`,
            severity: 'error',
          })
        );
      });
  };

export const [reopenAllStarted, reopenAllReceived, reopenAllFailed] = [
  createAction('ADMIN_REOPEN_ALL_STARTED'),
  createAction('ADMIN_REOPEN_ALL_RECEIVED'),
  createAction('ADMIN_REOPEN_ALL_FAILED'),
];

export const reopenAll = (coordinators) => (dispatch) => {
  dispatch(reopenAllStarted());
  const updates = {};

  Object.keys(coordinators).forEach((cid) => {
    updates['' + cid + '/finished'] = null;
    updates['' + cid + '/billing'] = null;
    updates['' + cid + '/students'] = null;
  });

  update(ref(database, '/koordinator/'), updates)
    .then(() => {
      dispatch(finishRegistrationReceived());
      dispatch(
        createNotification({
          message: `Otvorená registrácia`,
          severity: 'success',
        })
      );
    })
    .catch((error) => {
      dispatch(finishRegistrationFailed());
      dispatch(
        createNotification({
          message: `${error.code}: ${error.message}`,
          severity: 'error',
        })
      );
    });
};
